import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Sun, Moon } from 'lucide-react';
import { brandInfo } from '../data/bikes';
import { useLanguage } from '../i18n';
import { useTheme } from '../context/ThemeContext';

// Theme Toggle Component
const ThemeToggle = () => {
  const { isDark, toggleTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
        isDark 
          ? 'bg-[#1a1a25] border border-[#2a2a3a]' 
          : 'bg-[#B5A16A]/20 border border-[#B5A16A]/30'
      }`}
      data-testid="theme-toggle"
      aria-label="Toggle theme"
    >
      <div 
        className={`absolute top-0.5 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${
          isDark 
            ? 'left-0.5 bg-[#B5A16A]' 
            : 'left-7 bg-yellow-400'
        }`}
      >
        {isDark ? (
          <Moon className="w-3.5 h-3.5 text-white" />
        ) : (
          <Sun className="w-3.5 h-3.5 text-yellow-900" />
        )}
      </div>
    </button>
  );
};

// Language Selector Component
const LanguageSelector = () => {
  const { language, setLanguage, languages } = useLanguage();
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const currentLang = languages.find(l => l.code === language);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-full transition-colors ${
          isDark 
            ? 'bg-[#1a1a25] border border-[#2a2a3a] hover:border-[#B5A16A]/50' 
            : 'bg-white border border-gray-200 hover:border-[#B5A16A]/30 shadow-sm'
        }`}
        data-testid="language-selector"
      >
        <span className="text-xl">{currentLang?.flag}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isDark ? 'text-white/60' : 'text-gray-500'} ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className={`absolute right-0 top-full mt-2 rounded-xl overflow-hidden shadow-xl z-50 min-w-[140px] ${
          isDark 
            ? 'bg-[#12121a] border border-[#2a2a3a] shadow-black/50' 
            : 'bg-white border border-gray-200 shadow-gray-200/50'
        }`}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                isDark 
                  ? `hover:bg-[#B5A16A]/20 ${language === lang.code ? 'bg-[#B5A16A]/10 text-[#B5A16A]' : 'text-white/80'}`
                  : `hover:bg-[#B5A16A]/10 ${language === lang.code ? 'bg-[#B5A16A]/10 text-[#8a7a52]' : 'text-gray-700'}`
              }`}
              data-testid={`lang-${lang.code}`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className="text-sm font-medium">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MainHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const { t } = useLanguage();
  const { isDark } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Simplified navigation - only categories (Folding first)
  const navLinks = [
    { name: t('nav.folding'), href: isHomePage ? '#category-folding' : '/#category-folding' },
    { name: t('nav.fatbike'), href: isHomePage ? '#category-fatbike' : '/#category-fatbike' },
    { name: t('nav.mtb'), href: isHomePage ? '#category-mtb' : '/#category-mtb' },
    { name: t('nav.urban'), href: isHomePage ? '#category-urban' : '/#category-urban' },
  ];

  const handleNavClick = (e, href) => {
    // In-page scroll
    if (href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
      return;
    }

    // Cross-page (e.g. from /bikes/:slug to /#category-mtb)
    if (href.startsWith('/#')) {
      e.preventDefault();
      const hash = href.slice(1); // "#category-mtb"
      setIsMobileMenuOpen(false);
      navigate('/');
      // Wait a tick for Home to render, then scroll
      window.setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    }
  };

  const handleLogoClick = (e) => {
    // Always go to top (requested for sidebar/header logo)
    if (isHomePage) {
      e.preventDefault();
      setIsMobileMenuOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setIsMobileMenuOpen(false);
    navigate('/');
    window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'py-2' : 'py-4'
      }`}
      style={{ 
        background: isScrolled 
          ? (isDark ? 'rgba(10, 10, 15, 0.95)' : 'rgba(248, 250, 252, 0.95)') 
          : 'transparent' 
      }}
      data-testid="main-header"
    >
      <div className="container mx-auto px-4">
        <nav
          className={`flex items-center justify-between rounded-full px-4 md:px-6 py-3 transition-all duration-300 ${
            isDark
              ? isScrolled
                ? 'bg-[#12121a]/90 backdrop-blur-md border border-[#B5A16A]/20 shadow-lg shadow-[#B5A16A]/10'
                : 'bg-[#12121a]/70 backdrop-blur-sm border border-white/5'
              : isScrolled
                ? 'bg-white/90 backdrop-blur-md border border-gray-200 shadow-lg shadow-gray-200/50'
                : 'bg-white/70 backdrop-blur-sm border border-gray-100'
          }`}
        >
          {/* Logo */}
          <Link to="/" onClick={handleLogoClick} className="flex items-center gap-3" data-testid="logo-link">
            <span className={`inline-flex items-center justify-center ${isDark ? 'bg-white rounded-full p-1.5' : ''}`}>
              <img 
                src={brandInfo.logo} 
                alt={brandInfo.name}
                className={`h-8 md:h-10 drop-shadow-[0_0_10px_rgba(181,161,106,0.35)]`}
              />
            </span>
            <span className={`font-bold text-xl tracking-tight hidden sm:block ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {brandInfo.name}
            </span>
          </Link>

          {/* Desktop Navigation - Categories only */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`nav-link text-sm font-medium transition-colors relative group ${
                  isDark ? 'text-white/80 hover:text-[#B5A16A]' : 'text-gray-600 hover:text-[#8a7a52]'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 transition-all group-hover:w-full ${
                  isDark ? 'bg-[#B5A16A]' : 'bg-[#8a7a52]'
                }`} />
              </a>
            ))}
          </div>

          {/* Right Side: Theme Toggle + Language Selector */}
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <LanguageSelector />

            {/* Mobile Menu Button */}
            <button
              className={`lg:hidden p-2 ${isDark ? 'text-white' : 'text-gray-700'}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            className={`lg:hidden mt-2 backdrop-blur-md rounded-3xl p-6 shadow-xl ${
              isDark 
                ? 'bg-[#12121a]/95 border border-[#B5A16A]/20' 
                : 'bg-white/95 border border-gray-200'
            }`}
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className={`text-lg font-medium transition-colors ${
                    isDark ? 'text-white/80 hover:text-[#B5A16A]' : 'text-gray-700 hover:text-[#8a7a52]'
                  }`}
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default MainHeader;
